/**
 * Mapping of API errors onto react-hook-form fields.
 *
 * Field messages are always *codes* — from zod schemas (`z.string().min(1, 'REQUIRED')`)
 * or from the backend (`details.fields`) — and are rendered via i18n `validation.<CODE>`.
 */
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { isApiError } from '@/shared/api';

import ru from '@/shared/i18n/locales/ru.json';

const KNOWN_VALIDATION_CODES = new Set(Object.keys(ru.validation));

/** Translate a field error code; unknown codes fall back to a generic message. */
export function useFieldError(): (code: string | undefined) => string | undefined {
  const { t } = useTranslation();
  return (code) => {
    if (!code) return undefined;
    const key = KNOWN_VALIDATION_CODES.has(code) ? code : 'INVALID';
    return t(`validation.${key as keyof typeof ru.validation}`);
  };
}

/**
 * Put server-side validation errors on the matching form fields.
 * `codeFields` maps domain error codes to a field (e.g. LOGIN_TAKEN -> login).
 * Returns true if the error was attached to fields; otherwise the caller shows
 * it as a form-level error.
 */
export function applyApiErrorToForm<T extends FieldValues>(
  error: unknown,
  setError: UseFormSetError<T>,
  fields: readonly Path<T>[],
  codeFields: Partial<Record<string, Path<T>>> = {},
): boolean {
  if (!isApiError(error)) return false;
  const domainField = codeFields[error.code];
  if (domainField) {
    setError(domainField, { message: error.code });
    return true;
  }
  let applied = false;
  for (const [field, codes] of Object.entries(error.fieldErrors)) {
    if ((fields as readonly string[]).includes(field) && codes[0]) {
      setError(field as Path<T>, { message: codes[0] });
      applied = true;
    }
  }
  return applied;
}

/**
 * Server error handling for a form: field errors go onto fields, anything else
 * becomes `formError` (render it with `<FormError error={formError} />`).
 */
export function useServerErrors<T extends FieldValues>(
  setError: UseFormSetError<T>,
  fields: readonly Path<T>[],
  codeFields: Partial<Record<string, Path<T>>> = {},
) {
  const [formError, setFormError] = useState<unknown>(null);
  return {
    formError,
    clear: () => {
      setFormError(null);
    },
    handle: (error: unknown) => {
      setFormError(applyApiErrorToForm(error, setError, fields, codeFields) ? null : error);
    },
  };
}
